import { useEffect, useState } from "react";
import { adminApi } from "@/lib/api";
import { toast } from "sonner";
import { RefreshCw, ExternalLink, CheckCircle2, AlertTriangle, Save, FlaskConical, Loader2, HelpCircle, Globe, ArrowRight, Calendar } from "lucide-react";
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger,
} from "@/components/ui/dialog";

const Stat = ({ label, value, hint }) => (
    <div className="bg-liv-card border border-liv-border p-4">
        <div className="text-xs text-neutral-500 uppercase tracking-wider">{label}</div>
        <div className="text-2xl font-bold mt-1 tabular-nums">{value}</div>
        {hint ? <div className="text-xs text-neutral-500 mt-1">{hint}</div> : null}
    </div>
);

const MackolikSync = () => {
    const [settings, setSettings] = useState({
        sporzip_slug: "", team_display_name: "", enabled: true,
        auto_sync_enabled: false, auto_sync_day: "sun",
        auto_sync_hour: 0, auto_sync_minute: 0,
    });
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [testing, setTesting] = useState(false);
    const [syncing, setSyncing] = useState(false);
    const [testResult, setTestResult] = useState(null);
    const [syncOptions, setSyncOptions] = useState({
        standings: true, fixtures: true, squad: true,
    });

    const loadSettings = () => {
        setLoading(true);
        adminApi.mackolikSettings()
            .then((s) => setSettings({
                sporzip_slug: s.sporzip_slug || "",
                team_display_name: s.team_display_name || "",
                enabled: s.enabled !== false,
                auto_sync_enabled: !!s.auto_sync_enabled,
                auto_sync_day: s.auto_sync_day || "sun",
                auto_sync_hour: s.auto_sync_hour ?? 0,
                auto_sync_minute: s.auto_sync_minute ?? 0,
                last_sync_at: s.last_sync_at,
                last_sync_status: s.last_sync_status,
                last_sync_summary: s.last_sync_summary,
                last_sync_error: s.last_sync_error,
                last_sync_trigger: s.last_sync_trigger,
                next_auto_sync_at: s.next_auto_sync_at,
            }))
            .finally(() => setLoading(false));
    };

    useEffect(loadSettings, []);

    const update = (k, v) => setSettings((s) => ({ ...s, [k]: v }));
    const updateOpt = (k, v) => setSyncOptions((s) => ({ ...s, [k]: v }));

    const onSave = async () => {
        if (!settings.team_display_name) {
            toast.error("Takım adı zorunludur");
            return;
        }
        setSaving(true);
        try {
            await adminApi.saveMackolikSettings({
                sporzip_slug: settings.sporzip_slug.trim(),
                team_display_name: settings.team_display_name.trim(),
                enabled: settings.enabled,
                auto_sync_enabled: settings.auto_sync_enabled,
                auto_sync_day: settings.auto_sync_day,
                auto_sync_hour: Number(settings.auto_sync_hour),
                auto_sync_minute: Number(settings.auto_sync_minute),
            });
            toast.success("Ayarlar kaydedildi");
            loadSettings();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Kaydedilemedi");
        } finally {
            setSaving(false);
        }
    };

    const onTest = async () => {
        setTesting(true);
        setTestResult(null);
        try {
            const res = await adminApi.mackolikTest();
            setTestResult(res);
            toast.success(`Bağlantı başarılı — ${res.counts.standings} sıralama, ${res.counts.fixtures} maç, ${res.counts.squad} oyuncu bulundu`);
        } catch (e) {
            setTestResult({ ok: false, error: e?.response?.data?.detail || String(e) });
            toast.error(e?.response?.data?.detail || "Bağlantı başarısız");
        } finally {
            setTesting(false);
        }
    };

    const onSync = async () => {
        if (!window.confirm("Mevcut puan durumu ve fikstür silinip Sporzip'ten gelen yeni veri yazılacak. Emin misiniz?")) return;
        setSyncing(true);
        try {
            const res = await adminApi.mackolikSync(syncOptions);
            const ap = res.summary.applied;
            toast.success(
                `Yenileme tamam: ${ap.standings} sıralama, ${ap.fixtures} maç, ${ap.players_updated} oyuncu güncellendi (+${ap.players_created} yeni).`,
                { duration: 8000 },
            );
            loadSettings();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Yenileme başarısız");
        } finally {
            setSyncing(false);
        }
    };

    if (loading) return <div className="p-8 text-neutral-400">Yükleniyor...</div>;

    const last = settings.last_sync_summary;

    return (
        <div className="p-6 md:p-8 space-y-8 max-w-5xl" data-testid="admin-mackolik-page">
            <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
                <div>
                    <div className="flex items-center gap-3">
                        <RefreshCw className="w-6 h-6 text-liv-yellow" />
                        <h1 className="font-display text-3xl md:text-4xl uppercase">Fikstür & Puan Durumu</h1>
                    </div>
                    <p className="text-neutral-400 mt-2 text-sm md:text-base">
                        Puan durumu, fikstür ve kadro bilgilerini <b>Sporzip</b>'ten otomatik yenileyin. Tek tıkla, sezon süresince güncel kalın.
                    </p>
                </div>
                <HowToDialog />
            </div>

            {/* Settings */}
            <section className="bg-liv-card border border-liv-border p-6 md:p-8 space-y-5" data-testid="macko-settings">
                <h2 className="font-display text-xl uppercase tracking-wide">Takım Ayarları</h2>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                    <label className="block">
                        <span className="block text-xs uppercase tracking-wider text-neutral-400 mb-2">Sporzip Takım Adresi</span>
                        <input
                            value={settings.sporzip_slug}
                            onChange={(e) => update("sporzip_slug", e.target.value)}
                            placeholder="örn: livanespor"
                            className="liv-input w-full font-mono text-sm"
                            data-testid="sporzip-slug-input"
                        />
                        <span className="block text-xs text-neutral-500 mt-2">
                            Sporzip takım sayfasının adres sonu. Örnek: <span className="text-neutral-300">sporzip.com/<b>livanespor</b></span>. Boş bırakırsanız takım adından otomatik üretilir.
                        </span>
                    </label>

                    <label className="block">
                        <span className="block text-xs uppercase tracking-wider text-neutral-400 mb-2">Takım Adı (Görünen)</span>
                        <input
                            value={settings.team_display_name}
                            onChange={(e) => update("team_display_name", e.target.value)}
                            placeholder="örn: Livanespor"
                            className="liv-input w-full"
                            data-testid="macko-team-name-input"
                        />
                        <span className="block text-xs text-neutral-500 mt-2">
                            Sporzip'teki yazılışla bire bir aynı olmalı (fikstürde ev/deplasman ayırmak için kullanılıyor).
                        </span>
                    </label>
                </div>

                <label className="flex items-center gap-3 cursor-pointer">
                    <input
                        type="checkbox"
                        checked={settings.enabled}
                        onChange={(e) => update("enabled", e.target.checked)}
                        className="w-4 h-4 accent-liv-yellow"
                        data-testid="macko-enabled-toggle"
                    />
                    <span className="text-sm">Senkronizasyon açık</span>
                </label>

                <div className="flex flex-wrap gap-3 pt-2">
                    <button
                        onClick={onSave}
                        disabled={saving}
                        className="btn-primary inline-flex items-center gap-2 disabled:opacity-60"
                        data-testid="macko-save-btn"
                    >
                        {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                        Ayarları Kaydet
                    </button>
                    <button
                        onClick={onTest}
                        disabled={testing || !settings.team_display_name}
                        className="btn-secondary inline-flex items-center gap-2 disabled:opacity-60"
                        data-testid="macko-test-btn"
                    >
                        {testing ? <Loader2 className="w-4 h-4 animate-spin" /> : <FlaskConical className="w-4 h-4" />}
                        Bağlantı Testi (DB'ye yazmaz)
                    </button>
                </div>

                {testResult && testResult.ok && (
                    <div className="border border-emerald-700/50 bg-emerald-900/20 p-4 text-sm space-y-2" data-testid="macko-test-result-success">
                        <div className="flex items-center gap-2 text-emerald-400 font-semibold">
                            <CheckCircle2 className="w-4 h-4" /> Bağlantı başarılı
                        </div>
                        {testResult.competition && (
                            <div className="text-xs text-neutral-300">
                                Lig: <b className="text-liv-yellow">{testResult.competition}</b> · Sezon: <b>{testResult.season}</b>
                            </div>
                        )}
                        <div className="grid grid-cols-3 gap-3 mt-2">
                            <Stat label="Sıralama" value={testResult.counts.standings} />
                            <Stat label="Maç" value={testResult.counts.fixtures} />
                            <Stat label="Oyuncu" value={testResult.counts.squad} />
                        </div>
                        <div className="text-xs text-neutral-400 pt-2 space-y-1">
                            {Object.entries(testResult.urls || {}).map(([k, v]) => (
                                <div key={k} className="flex items-center gap-2">
                                    <span className="uppercase text-neutral-500 w-20">{k}:</span>
                                    <a href={v} target="_blank" rel="noreferrer" className="text-liv-yellow hover:underline truncate inline-flex items-center gap-1">
                                        {v} <ExternalLink className="w-3 h-3" />
                                    </a>
                                </div>
                            ))}
                        </div>
                    </div>
                )}
                {testResult && !testResult.ok && (
                    <div className="border border-red-700/50 bg-red-900/20 p-4 text-sm" data-testid="macko-test-result-error">
                        <div className="flex items-center gap-2 text-red-400 font-semibold mb-2">
                            <AlertTriangle className="w-4 h-4" /> Bağlantı başarısız
                        </div>
                        <pre className="text-xs whitespace-pre-wrap text-neutral-300">{testResult.error}</pre>
                    </div>
                )}
            </section>

            {/* Sync action */}
            <section className="bg-liv-card border border-liv-border p-6 md:p-8 space-y-5" data-testid="macko-sync-section">
                <h2 className="font-display text-xl uppercase tracking-wide">Verileri Yenile</h2>
                <p className="text-sm text-neutral-400">
                    Aşağıdakilerden hangilerini güncellemek istediğinizi seçin. Mevcut puan durumu ve fikstür <b>silinip yeniden yazılır</b>; oyuncular isim eşleşmesiyle güncellenir, eşleşmeyen yeni oyuncular eklenir, hiçbir oyuncu silinmez.
                </p>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                    {[
                        { k: "standings", label: "Puan Durumu", desc: "Güncel lig tablosu" },
                        { k: "fixtures", label: "Fikstür / Maçlar", desc: "Sezonun tüm maçları (oynanan + yaklaşan)" },
                        { k: "squad", label: "Kadro", desc: "Oyuncu adı, mevki, yaş" },
                    ].map(({ k, label, desc }) => (
                        <label key={k} className="flex items-start gap-3 p-4 border border-liv-border bg-liv-surface cursor-pointer hover:border-liv-yellow/50 transition-colors">
                            <input
                                type="checkbox"
                                checked={syncOptions[k]}
                                onChange={(e) => updateOpt(k, e.target.checked)}
                                className="w-4 h-4 mt-1 accent-liv-yellow"
                                data-testid={`macko-opt-${k}`}
                            />
                            <div>
                                <div className="text-sm font-semibold">{label}</div>
                                <div className="text-xs text-neutral-500 mt-1">{desc}</div>
                            </div>
                        </label>
                    ))}
                </div>

                <div className="bg-amber-900/20 border border-amber-700/50 p-3 text-xs text-neutral-300">
                    Sporzip oyuncu fotoğrafı, gol/asist istatistiği ve yaklaşan maçların saatini yayınlamıyor. Fotoğraf ve istatistikleri panelden elle girebilirsiniz; yaklaşan maç tarihleri haftalık olarak tahmin edilir, <b>Maçlar</b> sayfasından düzeltebilirsiniz (elle düzelttiğiniz tarihler senkronda korunur).
                </div>

                <button
                    onClick={onSync}
                    disabled={syncing || !settings.team_display_name}
                    className="btn-primary inline-flex items-center gap-2 text-base px-6 py-3 disabled:opacity-60"
                    data-testid="macko-sync-btn"
                >
                    {syncing ? <Loader2 className="w-5 h-5 animate-spin" /> : <RefreshCw className="w-5 h-5" />}
                    {syncing ? "Yenileniyor — Sporzip'e bağlanılıyor..." : "Verileri Yenile"}
                </button>
            </section>

            {/* Auto-sync schedule */}
            <section className="bg-liv-card border border-liv-border p-6 md:p-8 space-y-5" data-testid="macko-autosync">
                <div className="flex items-start justify-between gap-3 flex-wrap">
                    <div>
                        <h2 className="font-display text-xl uppercase tracking-wide flex items-center gap-2">
                            <Calendar className="w-5 h-5 text-liv-yellow" /> Otomatik Senkronizasyon
                        </h2>
                        <p className="text-sm text-neutral-400 mt-1">
                            Belirlediğiniz gün ve saatte sistem otomatik olarak yeni verileri çeker. Maçlar Cumartesi/Pazar oynanıyorsa Pazar gece 00:00 öneririz.
                        </p>
                    </div>
                    <label className="inline-flex items-center gap-3 cursor-pointer bg-liv-surface border border-liv-border px-4 py-2">
                        <input
                            type="checkbox"
                            checked={settings.auto_sync_enabled}
                            onChange={(e) => update("auto_sync_enabled", e.target.checked)}
                            className="w-4 h-4 accent-liv-yellow"
                            data-testid="macko-autosync-toggle"
                        />
                        <span className="text-sm font-semibold">{settings.auto_sync_enabled ? "Aktif" : "Devre Dışı"}</span>
                    </label>
                </div>

                <div className={`grid grid-cols-1 md:grid-cols-3 gap-4 ${settings.auto_sync_enabled ? "" : "opacity-50 pointer-events-none"}`}>
                    <label className="block">
                        <span className="block text-xs uppercase tracking-wider text-neutral-400 mb-2">Gün</span>
                        <select
                            value={settings.auto_sync_day}
                            onChange={(e) => update("auto_sync_day", e.target.value)}
                            className="liv-input w-full"
                            data-testid="macko-autosync-day"
                        >
                            <option value="mon">Pazartesi</option>
                            <option value="tue">Salı</option>
                            <option value="wed">Çarşamba</option>
                            <option value="thu">Perşembe</option>
                            <option value="fri">Cuma</option>
                            <option value="sat">Cumartesi</option>
                            <option value="sun">Pazar</option>
                        </select>
                    </label>
                    <label className="block">
                        <span className="block text-xs uppercase tracking-wider text-neutral-400 mb-2">Saat</span>
                        <select
                            value={settings.auto_sync_hour}
                            onChange={(e) => update("auto_sync_hour", parseInt(e.target.value, 10))}
                            className="liv-input w-full"
                            data-testid="macko-autosync-hour"
                        >
                            {Array.from({ length: 24 }, (_, h) => (
                                <option key={h} value={h}>{String(h).padStart(2, "0")}:00</option>
                            ))}
                        </select>
                    </label>
                    <label className="block">
                        <span className="block text-xs uppercase tracking-wider text-neutral-400 mb-2">Dakika</span>
                        <select
                            value={settings.auto_sync_minute}
                            onChange={(e) => update("auto_sync_minute", parseInt(e.target.value, 10))}
                            className="liv-input w-full"
                            data-testid="macko-autosync-minute"
                        >
                            {[0, 15, 30, 45].map((m) => (
                                <option key={m} value={m}>{String(m).padStart(2, "0")}</option>
                            ))}
                        </select>
                    </label>
                </div>

                <div className="text-xs text-neutral-500">Türkiye saati (Europe/Istanbul) ile çalışır. Ayarları "Ayarları Kaydet" dediğinizde devreye girer.</div>

                {settings.next_auto_sync_at && settings.auto_sync_enabled && (
                    <div className="bg-emerald-900/20 border border-emerald-700/50 p-4 text-sm flex items-center gap-3" data-testid="macko-next-run">
                        <CheckCircle2 className="w-4 h-4 text-emerald-400" />
                        <span className="text-neutral-200">
                            Sonraki otomatik senkronizasyon: <b className="text-emerald-400">{new Date(settings.next_auto_sync_at).toLocaleString("tr-TR", { weekday: "long", day: "numeric", month: "long", hour: "2-digit", minute: "2-digit", timeZone: "Europe/Istanbul" })}</b>
                            <span className="text-neutral-500"> (Türkiye saati)</span>
                        </span>
                    </div>
                )}
            </section>

            {/* Last sync status */}
            <section className="bg-liv-card border border-liv-border p-6 md:p-8 space-y-4" data-testid="macko-last-sync">
                <h2 className="font-display text-xl uppercase tracking-wide">Son Senkronizasyon</h2>
                {!settings.last_sync_at ? (
                    <p className="text-sm text-neutral-500">Henüz hiç senkronizasyon yapılmadı.</p>
                ) : (
                    <div className="space-y-4">
                        <div className="flex items-center gap-3 text-sm">
                            <span className={`inline-flex items-center gap-2 px-3 py-1 text-xs uppercase tracking-wider font-semibold ${settings.last_sync_status === "success" ? "bg-emerald-900/30 text-emerald-400 border border-emerald-700/50" : settings.last_sync_status === "error" ? "bg-red-900/30 text-red-400 border border-red-700/50" : "bg-yellow-900/30 text-yellow-400 border border-yellow-700/50"}`}>
                                {settings.last_sync_status === "success" ? <CheckCircle2 className="w-3 h-3" /> : <AlertTriangle className="w-3 h-3" />}
                                {settings.last_sync_status}
                            </span>
                            <span className="text-neutral-400">{new Date(settings.last_sync_at).toLocaleString("tr-TR")}</span>
                            {settings.last_sync_trigger && <span className="text-xs text-neutral-500">({settings.last_sync_trigger === "auto" ? "otomatik" : "manuel"})</span>}
                        </div>
                        {last && last.applied && (
                            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                                <Stat label="Sıralama" value={last.applied.standings} />
                                <Stat label="Maç" value={last.applied.fixtures} />
                                <Stat label="Oyuncu Güncel" value={last.applied.players_updated} hint={`+${last.applied.players_created} yeni`} />
                                <Stat label="Lig" value={last.competition || "-"} hint={last.season} />
                            </div>
                        )}
                        {settings.last_sync_error && (
                            <div className="border border-red-700/50 bg-red-900/20 p-4 text-xs text-red-300">
                                <div className="font-semibold mb-2">Hata:</div>
                                <pre className="whitespace-pre-wrap">{settings.last_sync_error}</pre>
                            </div>
                        )}
                    </div>
                )}
            </section>

            {/* Help footer link */}
            <section className="bg-liv-surface border border-liv-border p-5 text-sm text-neutral-400 flex flex-col md:flex-row md:items-center justify-between gap-3" data-testid="macko-help-footer">
                <div className="flex items-center gap-2">
                    <HelpCircle className="w-4 h-4 text-liv-yellow" />
                    <span>Takım sayfanızın adresini nasıl bulacağınızı bilmiyor musunuz?</span>
                </div>
                <HowToDialog variant="link" />
            </section>
        </div>
    );
};

// ─────────────────── How-To Dialog ───────────────────
const Step = ({ n, title, children }) => (
    <div className="flex gap-4">
        <div className="shrink-0 w-9 h-9 rounded-full bg-liv-yellow text-black font-display text-lg flex items-center justify-center">{n}</div>
        <div className="flex-1 pt-1">
            <h3 className="font-semibold text-neutral-100 mb-2">{title}</h3>
            <div className="text-sm text-neutral-400 space-y-2">{children}</div>
        </div>
    </div>
);

const Code = ({ children }) => (
    <code className="px-2 py-0.5 bg-black/40 border border-liv-border text-liv-yellow font-mono text-xs break-all">{children}</code>
);

const HowToDialog = ({ variant = "button" }) => (
    <Dialog>
        <DialogTrigger asChild>
            {variant === "link" ? (
                <button className="text-liv-yellow hover:underline inline-flex items-center gap-1 text-sm font-semibold" data-testid="macko-howto-link">
                    Adım adım rehberi aç <ArrowRight className="w-3 h-3" />
                </button>
            ) : (
                <button className="btn-secondary inline-flex items-center gap-2 self-start" data-testid="macko-howto-btn">
                    <HelpCircle className="w-4 h-4" />
                    Veriler Nasıl Çekilir?
                </button>
            )}
        </DialogTrigger>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto bg-liv-card border-liv-border" data-testid="macko-howto-dialog">
            <DialogHeader>
                <DialogTitle className="font-display text-2xl md:text-3xl uppercase tracking-wide">Fikstür & Puan Durumu Çekme Rehberi</DialogTitle>
                <DialogDescription className="text-neutral-400">
                    Veriler <b>sporzip.com</b> üzerinden çekilir — amatör ligler dahil tüm Türkiye ligleri mevcuttur.
                </DialogDescription>
            </DialogHeader>

            <div className="space-y-7 mt-4">
                <Step n={1} title="Sporzip'te takımınızı bulun">
                    <p>
                        <a href="https://sporzip.com" target="_blank" rel="noreferrer" className="text-liv-yellow hover:underline inline-flex items-center gap-1">sporzip.com <ExternalLink className="w-3 h-3" /></a> adresine gidin ve arama kutusuna takımınızın adını yazın.
                    </p>
                    <p className="text-xs text-neutral-500">Çıkan sonuçlardan <b>takım sayfasına</b> girin (oyuncu veya lig değil).</p>
                </Step>

                <Step n={2} title="Adres sonundaki kısmı kopyalayın">
                    <div className="bg-black/40 border border-liv-border p-3 font-mono text-xs break-all leading-relaxed">
                        https://sporzip.com/<span className="text-liv-yellow font-bold">livanespor</span>
                    </div>
                    <p>Sarı kısım = <b>Sporzip Takım Adresi</b>. Bunu forma yapıştırın. Örnekler: <Code>livanespor</Code> · <Code>bursa-karincaligucu</Code> · <Code>orhangazi-cakirlispor</Code></p>
                </Step>

                <Step n={3} title="Takım adını birebir girin">
                    <p>"Takım Adı (Görünen)" alanına Sporzip'te yazıldığı şekliyle aynen yazın (örn. <span className="text-emerald-400">Livanespor</span>). Bu ad fikstürde ev sahibi / deplasman ayrımı için kullanılır.</p>
                    <p>Sonra <span className="text-liv-yellow font-semibold">"Ayarları Kaydet"</span> deyin.</p>
                </Step>

                <Step n={4} title="Bağlantı Testi ile doğrulayın">
                    <p>Bu buton <b>veritabanına hiçbir şey yazmaz</b>; sadece kaç sıralama / maç / oyuncu bulduğunu ve hangi ligde göründüğünüzü gösterir.</p>
                    <ul className="list-disc list-inside space-y-1 text-xs">
                        <li>Sıralama 0 ise → Takım adresi (slug) yanlış olabilir</li>
                        <li>Maç 0 ise → Sezon henüz başlamamış olabilir</li>
                    </ul>
                </Step>

                <Step n={5} title='"Verileri Yenile" butonuna basın'>
                    <div className="bg-amber-900/20 border border-amber-700/50 p-3 text-xs space-y-1">
                        <div className="flex items-center gap-2 text-amber-300 font-semibold"><AlertTriangle className="w-3 h-3" /> Önemli</div>
                        <ul className="list-disc list-inside text-neutral-300 space-y-1">
                            <li><b>Puan Durumu</b> ve <b>Fikstür</b> tamamen silinip yeniden yazılır (lig değiştiyse yeni lig otomatik gelir).</li>
                            <li><b>Oyuncular</b> isim eşleşmesiyle güncellenir; <b>hiçbir oyuncu silinmez</b>.</li>
                            <li>Yaklaşan maçların tarihi haftalık tahmin edilir; <b>Maçlar</b> sayfasından elle düzelttikleriniz korunur.</li>
                        </ul>
                    </div>
                </Step>

                <Step n={6} title="Otomatik senkronu açın">
                    <p>"Otomatik Senkronizasyon" bölümünden gün ve saati seçip aktif edin — her hafta otomatik güncellenir, bu sayfaya gelmenize gerek kalmaz.</p>
                </Step>

                <div className="border-t border-liv-border pt-5 mt-2">
                    <div className="flex items-start gap-3 text-sm">
                        <Globe className="w-5 h-5 text-liv-yellow shrink-0 mt-0.5" />
                        <div>
                            <div className="font-semibold text-neutral-100">White-label kullanım</div>
                            <p className="text-neutral-400 mt-1">
                                Her kulüp kendi takım adresini girer; aynı sistem 50+ kulüp için paralel çalışır ve her kulübün otomatik senkronu kendi takvimiyle işler.
                            </p>
                        </div>
                    </div>
                </div>
            </div>
        </DialogContent>
    </Dialog>
);

export default MackolikSync;
